// 역할 룰렛 게임용 소켓 통신 서비스
// 라벨 세팅, 배정, 시작/종료 등 소켓 메시지 송수신 구현 예정

// 예시: export function sendRoleLabels, onRoleAssigned 등

import {
  DefaultRole,
  RoleDistributorAction,
  SpecialRole,
} from "./RoleDistributorReducer";
import {
  RoleDistributorProtocol,
  RoleDistributorProtocolData,
} from "./RoleDistributorProtocol";
import { AbilityUsage } from "./abilities";
import SocketService from "../../network/SocketService";

export class RoleDistributorSocketService {
  private dispatch: React.Dispatch<RoleDistributorAction>;
  private socket = SocketService.getInstance();

  constructor(dispatch: React.Dispatch<RoleDistributorAction>) {
    this.dispatch = dispatch;
  }

  public initializeListeners() {
    // 페이즈 준비 (플레이어 목록 포함) - 서버에서 자동으로 전송
    this.socket.on(
      RoleDistributorProtocol.PHASE_READY,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.PHASE_READY]
      ) => {
        // 플레이어 목록 설정
        this.dispatch({
          type: "SET_PLAYERS",
          players: data.players,
        });

        // 방장 ID 설정
        this.dispatch({
          type: "SET_HOST",
          hostId: data.hostId,
        });

        // 라벨 목록 설정
        this.dispatch({
          type: "SET_LABELS",
          labels: data.labels,
        });

        // 기본 라벨 설정
        this.dispatch({
          type: "SET_DEFAULT_LABEL",
          defaultLabel: data.defaultLabel,
        });

        // 호스트 공개 모드 설정
        this.dispatch({
          type: "SET_HOST_REVEAL",
          value: data.hostReveal,
        });

        // 게임 시작 상태 설정
        this.dispatch({
          type: "SET_STARTED",
          started: data.started,
        });

        // 설정 완료 상태 설정
        this.dispatch({
          type: "SET_SETTINGS_COMPLETED",
          completed: data.settingsCompleted,
        });

        // 특수능력 단계 상태 설정
        if (data.abilityPhase) {
          if (data.abilityPhase.isActive) {
            this.dispatch({
              type: "START_ABILITY_PHASE",
              startedBy: data.abilityPhase.startedBy,
              duration: data.abilityPhase.duration,
              endTime: data.abilityPhase.endTime,
            });
          } else {
            this.dispatch({
              type: "END_ABILITY_PHASE",
            });
          }
        }

        // 능력 사용 기록 설정
        if (data.abilityUsages) {
          data.abilityUsages.forEach((usage) => {
            this.dispatch({
              type: "USE_ABILITY",
              usage,
            });
          });
        }
      }
    );

    // 플레이어 입장
    this.socket.on(
      RoleDistributorProtocol.PLAYER_JOINED,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.PLAYER_JOINED]
      ) => {
        this.dispatch({
          type: "ADD_PLAYER",
          player: data.player,
        });
      }
    );

    // 플레이어 퇴장
    this.socket.on(
      RoleDistributorProtocol.PLAYER_LEFT,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.PLAYER_LEFT]
      ) => {
        this.dispatch({
          type: "REMOVE_PLAYER",
          playerId: data.playerId,
        });
      }
    );

    // 역할 룰렛 시작
    this.socket.on(
      RoleDistributorProtocol.DISTRIBUTION_START,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.DISTRIBUTION_START]
      ) => {
        this.dispatch({
          type: "START_DISTRIBUTION",
          players: data.players,
        });
      }
    );

    // 역할 룰렛 종료
    this.socket.on(
      RoleDistributorProtocol.DISTRIBUTION_END,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.DISTRIBUTION_END]
      ) => {
        // 서버에서 이미 역할이 제거된 플레이어 목록을 받으므로
        // 플레이어 목록 업데이트와 게임 상태 변경만 수행
        this.dispatch({
          type: "SET_PLAYERS",
          players: data.players,
        });
        this.dispatch({
          type: "SET_STARTED",
          started: false,
        });
        // 게임 종료 시 능력 히스토리 초기화
        this.dispatch({
          type: "CLEAR_ABILITY_HISTORY",
        });
      }
    );

    // 역할 재배정 (룰렛 종료 후 새로운 배정)
    this.socket.on(
      RoleDistributorProtocol.ROLES_ASSIGNED,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.ROLES_ASSIGNED]
      ) => {
        this.dispatch({
          type: "START_DISTRIBUTION",
          players: data.players,
        });
      }
    );

    // 설정 완료
    this.socket.on(
      RoleDistributorProtocol.SETTINGS_COMPLETED,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.SETTINGS_COMPLETED]
      ) => {
        this.dispatch({
          type: "COMPLETE_SETTINGS",
          defaultLabel: data.defaultLabel,
          labels: data.labels,
          hostReveal: data.hostReveal,
        });
      }
    );

    // 설정 초기화
    this.socket.on(
      RoleDistributorProtocol.SETTINGS_RESET,
      () => {
        this.dispatch({
          type: "RESET_SETTINGS",
        });
      }
    );

    // 특수능력 단계 시작
    this.socket.on(
      RoleDistributorProtocol.ABILITY_PHASE_START,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.ABILITY_PHASE_START]
      ) => {
        this.dispatch({
          type: "START_ABILITY_PHASE",
          startedBy: data.startedBy,
          duration: data.duration,
          endTime: data.endTime,
        });
      }
    );

    // 액션 취소
    this.socket.on(
      RoleDistributorProtocol.ABILITY_CANCELED,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.ABILITY_CANCELED]
      ) => {
        this.dispatch({
          type: "CANCEL_ABILITY",
          playerId: data.playerId,
          abilityId: data.abilityId,
        });
      }
    );

    // 특수능력 단계 종료
    this.socket.on(
      RoleDistributorProtocol.ABILITY_PHASE_END,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.ABILITY_PHASE_END]
      ) => {
        // 능력 단계 종료
        this.dispatch({
          type: "END_ABILITY_PHASE",
        });

        // 결과가 있으면 히스토리에 추가
        if (data.results && data.results.length > 0) {
          this.dispatch({
            type: "ADD_ABILITY_HISTORY",
            phaseResult: {
              timestamp: data.timestamp || Date.now(),
              results: data.results,
            },
          });
        }
      }
    );

    // 능력 사용
    this.socket.on(
      RoleDistributorProtocol.ABILITY_USED,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.ABILITY_USED]
      ) => {
        this.dispatch({
          type: "USE_ABILITY",
          usage: data.usage,
        });
      }
    );

    // 능력 사용 결과
    this.socket.on(
      RoleDistributorProtocol.ABILITY_RESULT,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.ABILITY_RESULT]
      ) => {
        // 결과를 히스토리에 추가
        if (data.result) {
          this.dispatch({
            type: "ADD_ABILITY_HISTORY",
            phaseResult: {
              timestamp: Date.now(),
              results: [{
                message: data.result,
                type: "private" as const,
                targetPlayerId: data.usage?.playerId,
                abilityId: data.usage?.abilityId,
                success: data.success,
              }],
            },
          });
        }
      }
    );

    // 투표 결과 집계
    this.socket.on(
      RoleDistributorProtocol.VOTING_RESULT,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.VOTING_RESULT]
      ) => {
        // TODO: 투표 결과를 사용자에게 표시하는 로직 추가
        // - PUBLIC 타입: 개별 투표가 이미 공개되어 있으므로 집계만 표시
        // - PRIVATE 타입: 집계 결과만 표시 (개별 투표는 비공개)
      }
    );

    // 배치 결과 처리
    this.socket.on(
      RoleDistributorProtocol.ABILITY_RESULTS_BATCH,
      (
        data: RoleDistributorProtocolData[typeof RoleDistributorProtocol.ABILITY_RESULTS_BATCH]
      ) => {
        // 처리된 결과를 상태에 추가
        this.dispatch({
          type: "PROCESS_PENDING_RESULTS",
          processedResults: data.processedResults,
        });

        // 배치 결과도 히스토리에 추가 (특히 secret_vote_choice를 위해)
        if (data.processedResults && data.processedResults.length > 0) {
          const historyResults = data.processedResults.map((result) => ({
            message: result.result?.message || "Result processed",
            type: (result.result?.isPublic ? "public" : "private") as "public" | "private",
            targetPlayerId: result.result?.targetPlayerId,
            abilityId: result.abilityId,
            success: result.result?.success ?? true,
          }));

          this.dispatch({
            type: "ADD_ABILITY_HISTORY",
            phaseResult: {
              timestamp: Date.now(),
              results: historyResults,
            },
          });
        }

        // TODO: 결과 모달 표시 로직 추가
        // 이는 컴포넌트 레벨에서 처리되어야 함
      }
    );

    // 에러 처리
    this.socket.on(
      RoleDistributorProtocol.ERROR,
      (
        error: RoleDistributorProtocolData[typeof RoleDistributorProtocol.ERROR]
      ) => {
        console.error("RoleDistributor Socket Error:", error);
        // TODO: 에러 처리 로직 추가 (예: 토스트 메시지)
      }
    );
  }

  public cleanup() {
    this.socket.off(RoleDistributorProtocol.PHASE_READY);
    this.socket.off(RoleDistributorProtocol.PLAYER_JOINED);
    this.socket.off(RoleDistributorProtocol.PLAYER_LEFT);
    this.socket.off(RoleDistributorProtocol.DISTRIBUTION_START);
    this.socket.off(RoleDistributorProtocol.DISTRIBUTION_END);
    this.socket.off(RoleDistributorProtocol.ROLES_ASSIGNED);
    this.socket.off(RoleDistributorProtocol.SETTINGS_COMPLETED);
    this.socket.off(RoleDistributorProtocol.SETTINGS_RESET);
    this.socket.off(RoleDistributorProtocol.ABILITY_PHASE_START);
    this.socket.off(RoleDistributorProtocol.ABILITY_PHASE_END);
    this.socket.off(RoleDistributorProtocol.ABILITY_USED);
    this.socket.off(RoleDistributorProtocol.ABILITY_CANCELED);
    this.socket.off(RoleDistributorProtocol.ABILITY_RESULT);
    this.socket.off(RoleDistributorProtocol.ABILITY_RESULTS_BATCH);
    this.socket.off(RoleDistributorProtocol.VOTING_RESULT);
    this.socket.off(RoleDistributorProtocol.ERROR);
  }

  // 역할 룰렛 시작 요청
  public startDistribution(
    roomId: string,
    defaultRole: DefaultRole,
    specialRoles: SpecialRole[]
  ) {
    this.socket.emit(RoleDistributorProtocol.DISTRIBUTION_START, {
      roomId,
      defaultRole,
      specialRoles,
    });
  }

  // 역할 룰렛 종료 요청
  public endDistribution(roomId: string) {
    this.socket.emit(RoleDistributorProtocol.DISTRIBUTION_END, { roomId });
  }

  // 설정 완료 (방장용)
  public completeSettings(
    roomId: string,
    defaultLabel: { name: string; explain: string },
    labels: any[],
    hostReveal: boolean
  ) {
    const payload = {
      roomId,
      defaultLabel,
      labels,
      hostReveal,
    };
    this.socket.emit(RoleDistributorProtocol.SETTINGS_COMPLETED, payload);
  }

  // 설정 초기화 (방장용)
  public resetSettings(roomId: string) {
    this.socket.emit(RoleDistributorProtocol.SETTINGS_RESET, {
      roomId,
    });
  }

  // 특수능력 단계 시작 (방장용)
  public startAbilityPhase(startedBy: number, duration?: number) {
    const startTime = Date.now();
    this.socket.emit(RoleDistributorProtocol.ABILITY_PHASE_START, {
      startedBy,
      startTime,
      duration,
      endTime: duration ? startTime + duration * 1000 : undefined,
    });
  }

  // 특수능력 단계 종료 (방장용)
  public endAbilityPhase(endedBy: number) {
    this.socket.emit(RoleDistributorProtocol.ABILITY_PHASE_END, {
      endedBy,
      totalUsages: 0, // 서버에서 계산하도록 하거나 클라이언트에서 계산
    });
  }

  // 능력 사용
  public useAbility(usage: AbilityUsage) {
    this.socket.emit(RoleDistributorProtocol.ABILITY_USED, {
      usage,
      playerId: usage.playerId,
      abilityId: usage.abilityId,
      targetIds: usage.targetIds,
      choice: usage.choice,
      selectedRole: usage.selectedRole,
      selectedRoles: usage.selectedRoles,
    });
  }

  // 액션 취소
  public cancelAbility(playerId: number, abilityId: string) {
    this.socket.emit(RoleDistributorProtocol.ABILITY_CANCELED, {
      playerId,
      abilityId,
      timestamp: Date.now(),
    });
  }
}
